import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import axios from "axios";
import { useCurrentLocation } from "./useCurrentLocation";

interface ITrendingDrops {
  name: string;
  data: number[];
}

interface ITotalStreamPercentage extends ITrendingDrops {}

interface IAverageDaysBetweenDrops extends ITrendingDrops {
  _id?: string;
}

interface IAverageDropSize {
  name: string;
  data: number[];
}

interface IActiveUsersBasedOnDrops {
  usersWithOneDrop: {
    [key: string]: number;
  };
  usersWithTwoDrops: {
    [key: string]: number;
  };
  usersWithMoreThanThreeDrops: {
    [key: string]: number;
  };
}

interface IStore {
  averageDaysBetweenDrops: IAverageDaysBetweenDrops[] | null;
  streamPercentageOfAllTime: ITotalStreamPercentage[] | null;
  dropsNumberStat: ITrendingDrops[] | null;
  averageDropSizes: IAverageDropSize[] | null;
  activeUsersBasedOnDrops: IActiveUsersBasedOnDrops | null;
  materialsRevenuePerLocation: ITrendingDrops[];
  materialsRevenuePerMaterialPerLocation: Array<{
    name: string;
    data: Array<[string, number]>;
  }> | null;
  isMaterialsRevenuePerMaterialLoading: boolean;
  isMaterialsRevenuePerMaterialError: boolean;
  getNumberOfDropsStat: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly"
  ) => void;
  getActiveUsersBasedOnDrops: (requestedDay?: Date) => void;
  getAverageDropSize: (
    requestedDay?: Date,
    period?: "weekly" | "lastTwelweMonths" | "monthly" | "yearly"
  ) => void;
  getStreamPercentageOfAllTime: () => void;
  getAverageDaysBetweenDrops: () => void;
  getMaterialsRevenuePerLocation: (
    period: "month" | "week" | "quarter",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getMaterialsRevenuePerMaterialPerLocation: (
    period: "month" | "week" | "quarter",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    places?: string[]
  ) => void;
}

export const useDropStats = create<IStore>()(
  persist(
    (set) => ({
      /** State */
      averageDaysBetweenDrops: null,
      streamPercentageOfAllTime: null,
      dropsNumberStat: null,
      averageDropSizes: null,
      activeUsersBasedOnDrops: null,
      materialsRevenuePerLocation: [],
      materialsRevenuePerMaterialPerLocation: null,
      isMaterialsRevenuePerMaterialLoading: false,
      isMaterialsRevenuePerMaterialError: false,
      /** Functions */
      getNumberOfDropsStat: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/stats/number-of-drops?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ dropsNumberStat: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getActiveUsersBasedOnDrops: async (requestedDay = new Date()) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/stats/active-users-based-on-drops?requestedDay=${requestedDay}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ activeUsersBasedOnDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching active users based on drops", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getAverageDropSize: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/stats/average-drop-size?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ averageDropSizes: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching average drop size", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getStreamPercentageOfAllTime: async () => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/stats/stream-percentage-alltime?location=${currentLocation}`
          );
          if (response?.data) {
            set({ streamPercentageOfAllTime: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching stream percentage of all time", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getAverageDaysBetweenDrops: async () => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/stats/average-days-between-drops?location=${currentLocation}`
          );
          if (response?.data) {
            set({ averageDaysBetweenDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching average days between drops", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getMaterialsRevenuePerLocation: async (
        period: "month" | "week" | "quarter",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        places?: string[]
      ) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (places && places.length > 0) params.places = places.join(",");
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/drops/stats/materials-revenue-per-location?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ materialsRevenuePerLocation: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching Materials Revenue Per Location stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getMaterialsRevenuePerMaterialPerLocation: async (
        period,
        fromToParams,
        type?: "location" | "city",
        places?: string[]
      ) => {
        useAppLoading.getState().setIsLoading(true);
        set({
          materialsRevenuePerMaterialPerLocation: null,
          isMaterialsRevenuePerMaterialLoading: true,
          isMaterialsRevenuePerMaterialError: false,
        });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (places && places.length > 0) params.places = places.join(",");
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/drops/stats/materials-revenue-per-material-per-location?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ materialsRevenuePerMaterialPerLocation: response?.data });
          }
        } catch (error) {
          LOG.error(
            "Error on fetching Materials Revenue Per Material Per Location stats",
            { error }
          );
          set({
            materialsRevenuePerMaterialPerLocation: null,
            isMaterialsRevenuePerMaterialError: true,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
          set({ isMaterialsRevenuePerMaterialLoading: false });
        }
      },
    }),
    {
      name: "dashboard-stats",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) => key !== "isMaterialsRevenuePerMaterialLoading"
          )
        ),
    }
  )
);
